"use client";

import { capAll } from "@/lib/strapi/formatters";
import { Badge, Text } from "@mantine/core";
import { ExamBreakdown } from "@tsTypes/exam-breakdown-schema";
import SubjectCardTable from "../../../components/tables/SubjectCardTable";
import classes from "./ExamTable.module.css";

export default function ExamTable({
  examBreakdown,
  documentId,
  subjectNameLower,
  className,
}: {
  examBreakdown: ExamBreakdown;
  documentId: string;
  subjectNameLower: string;
  className?: string;
}) {
  const {
    PaperType,
    PaperNumber,
    ExamDetails,
    PaperContent,
    IsCalculatorAllowed,
    IsDataBookletAllowed,
  } = examBreakdown;

  const description = `This IB ${capAll(
    subjectNameLower
  )} exam paper tests all of your ${PaperContent} knowledge through ${PaperType.toLowerCase()} questions.<p></p>You are ${
    IsCalculatorAllowed ? "" : "not"
  } allowed a calculator ${
    IsDataBookletAllowed ? "and" : "nor a"
  } data booklet for this ${capAll(subjectNameLower)} exam.`;

  const rows = [];
  if (ExamDetails.SL) {
    rows.push({
      first: (
        <Badge
          size="lg"
          variant="outline"
          radius="sm"
          className={classes.levelBadge}
        >
          <span className={classes.levelBadgeText}>SL</span>
        </Badge>
      ),
      second: (
        <Text
          component="div"
          className={classes.descriptionText}
          dangerouslySetInnerHTML={{
            __html: `<span>${ExamDetails.SL.TotalMarks} marks:</span>${ExamDetails.SL.Description}`,
          }}
        />
      ),
      third: (
        <Text
          component="div"
          className={classes.descriptionText}
          dangerouslySetInnerHTML={{
            __html: `<span>${ExamDetails.SL.TotalTime} minutes:</span><ul>
              <li>${(
                ExamDetails.SL.TotalTime / ExamDetails.SL.TotalMarks
              ).toFixed(1)} minutes per mark</li>
            </ul>`,
          }}
        />
      ),
    });
  }
  if (ExamDetails.HL) {
    rows.push({
      first: (
        <Badge
          size="lg"
          variant="outline"
          radius="sm"
          className={classes.levelBadge}
        >
          <span className={classes.levelBadgeText}>HL</span>
        </Badge>
      ),
      second: (
        <Text
          component="div"
          className={classes.descriptionText}
          dangerouslySetInnerHTML={{
            __html: `<span>${ExamDetails.HL.TotalMarks} marks:</span>${ExamDetails.HL.Description}`,
          }}
        />
      ),
      third: (
        <Text
          component="div"
          className={classes.descriptionText}
          dangerouslySetInnerHTML={{
            __html: `<span>${ExamDetails.HL.TotalTime} minutes:</span><ul>
              <li>${(
                ExamDetails.HL.TotalTime / ExamDetails.HL.TotalMarks
              ).toFixed(1)} minutes per mark</li>
            </ul>`,
          }}
        />
      ),
    });
  }

  const weightParts: string[] = [];
  if (ExamDetails.SL) weightParts.push(`SL ${ExamDetails.SL.TotalWeighting}%`);
  if (ExamDetails.HL) weightParts.push(`HL ${ExamDetails.HL.TotalWeighting}%`);

  return (
    <SubjectCardTable
      id={documentId}
      className={className}
      header={{
        title: `Paper ${PaperNumber}: ${PaperType}`,
        badge: `Weighting: ${weightParts.join(" | ")}`,
        description,
      }}
      columns={{ first: "Level", second: "Description", third: "Time" }}
      rows={rows}
      firstColumnWidth="10%"
    />
  );
}
